import {
  ContentFeedNode,
  DTConditionNode,
  DTNode,
  DTOptimizationNode,
  DTProductSelectionNode,
  DTScheduleNode,
  DTSequenceNode,
  DTWeightedNode,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {z} from 'zod';

import {conditionCardSchema} from '../../../../../schemas/conditionCardSchema';
import {contentCardSchema} from '../../../../../schemas/contentCardSchema';
import {creativeCardSchema} from '../../../../../schemas/creativeCardSchema';
import {locationCardSchema} from '../../../../../schemas/locationCardSchema';
import {optimizationCardSchema} from '../../../../../schemas/optimizationCardSchema';
import {productCardSchema} from '../../../../../schemas/productCardSchema';
import {scheduleCardSchema} from '../../../../../schemas/scheduleCardSchema';
import {sequenceCardSchema} from '../../../../../schemas/sequenceCardSchema';
import {weightedCardSchema} from '../../../../../schemas/weightedCardSchema';
import {convertConditionAICardToAPICard} from '../../schemaConverters/conditionCard/convertConditionAICardToAPICard';
import {convertContentAICardToAPICard} from '../../schemaConverters/contentCard/convertContentAICardToAPICard';
import {convertLocationAICardToAPICard} from '../../schemaConverters/locationCard/convertLocationAICardToAPICard';
import {convertOptimizationAICardToAPICard} from '../../schemaConverters/optimizationCard/convertOptimizationAICardToAPICard';
import {convertProductAICardToAPICard} from '../../schemaConverters/productCard/convertProductAICardToAPICard';
import {convertScheduleAICardToAPICard} from '../../schemaConverters/scheduleCard/convertScheduleAICardToAPICard';
import {convertSequenceAICardToAPICard} from '../../schemaConverters/sequenceCard/convertSequenceAICardToAPICard';
import {convertWeightedAICardToAPICard} from '../../schemaConverters/weightedCard/convertWeightedAICardToAPICard';
import {DefaultCardOutputs} from './convertCardSchemaToCard.consts';

import {sharedCampaigns} from '@cl/shared';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {convertCreativeAICardToAPICard} from '../../schemaConverters/creativeCard/convertCreativeAICardToAPICard';

type CardData =
  | z.infer<typeof optimizationCardSchema>
  | z.infer<typeof weightedCardSchema>
  | z.infer<typeof creativeCardSchema>
  | z.infer<typeof scheduleCardSchema>
  | z.infer<typeof conditionCardSchema>
  | z.infer<typeof sequenceCardSchema>
  | z.infer<typeof contentCardSchema>
  | z.infer<typeof locationCardSchema>
  | z.infer<typeof productCardSchema>;

export default function convertCardSchemaToCard(card: CardData, cardId: number) {
  const existingCard = sharedCampaigns.nodesRef.value[cardId];

  const baseNodeData: DTNode = {
    id: cardId,
    type: card.type,
    outputs: [],
    inputs:
      existingCard?.inputs.map((input) => ({
        ...input,
        node: null,
        nodeId: input.node?.id ?? NO_NODE_ID,
      })) ?? [],
  };

  switch (card.type) {
    case NodeType.WEIGHTED:
      return {
        ...baseNodeData,
        ...convertWeightedAICardToAPICard(card, existingCard as DTWeightedNode),
      };
    case NodeType.GENERAL_OPTIMIZATION:
      return {
        ...baseNodeData,
        ...convertOptimizationAICardToAPICard(card, existingCard as DTOptimizationNode),
      };
    case NodeType.SCHEDULE:
      return {
        ...baseNodeData,
        ...convertScheduleAICardToAPICard(card, existingCard as DTScheduleNode),
      };
    case NodeType.CONDITION:
      return {
        ...baseNodeData,
        ...convertConditionAICardToAPICard(card, existingCard as DTConditionNode),
      };
    case NodeType.SEQUENCE:
      return {
        ...baseNodeData,
        ...convertSequenceAICardToAPICard(card, existingCard as DTSequenceNode),
      };
    case NodeType.CONTENT_FEED:
      return {
        ...baseNodeData,
        ...convertContentAICardToAPICard(card, existingCard as ContentFeedNode),
      };
    case NodeType.STORE:
      return {
        ...baseNodeData,
        ...convertLocationAICardToAPICard(card),
        outputs: existingCard?.outputs ?? DefaultCardOutputs,
      };
    case NodeType.PRODUCTS:
      return {
        ...baseNodeData,
        ...convertProductAICardToAPICard(card, existingCard as DTProductSelectionNode),
      };
    case NodeType.CREATIVE:
      return {
        ...baseNodeData,
        ...convertCreativeAICardToAPICard(card),
      };
    default:
      throw new Error('Invalid card type');
  }
}
